import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useLocation, useNavigationType } from "react-router-dom";
import { ThemeProvider } from "next-themes";
import { useEffect } from "react";
import Index from "./pages/Index";
import Home from "./pages/Home";
import AboutPage from "./pages/About";
import ResourcesPage from "./pages/Resources";
import ProcessesPage from "./pages/Processes";
import DIYProcessesPage from "./pages/DIYProcesses";
import VideoLibrary from "./pages/VideoLibrary";
import VideoCategoryPage from "./pages/VideoCategoryPage";
import VideoDetailPage from "./pages/VideoDetailPage";
import CategoryOptionsPage from "./pages/CategoryOptionsPage";
import CategoryDocumentsPage from "./pages/CategoryDocumentsPage";
import CategoryForumPage from "./pages/CategoryForumPage";
import DIYProcessPage from "./pages/DIYProcessPage";
import DocumentsTemplates from "./pages/DocumentsTemplates";
import WorkWithUsPage from "./pages/WorkWithUs";
import LitigationPage from "./pages/Litigation";
import PhoneConsultationPage from "./pages/PhoneConsultation";
import MaximsPage from "./pages/Maxims";
import MaximsOfEquityPage from "./pages/MaximsOfEquity";
import MaximsOverviewPage from "./pages/MaximsOverview";
import DictionaryPage from "./pages/Dictionary";
import DictionaryTerm from "./pages/DictionaryTerm";
import UCCPage from "./pages/UCC";
import FAQPage from "./pages/FAQ";

import NotFound from "./pages/NotFound";
import AttorneyInFactPage from "./pages/services/AttorneyInFact";
import PoliticalStatusCorrectionPage from "./pages/services/PoliticalStatusCorrection";
import PrivateTrustPage from "./pages/services/PrivateTrust";
import TaxLiabilityPage from "./pages/services/TaxLiability";
import NonCitizenPassportPage from "./pages/services/NonCitizenPassport";
import ForeignTrustBankingPage from "./pages/services/ForeignTrustBanking";
import DOTAuthorityPage from "./pages/services/DOTAuthority";
import DischargeLoanPage from "./pages/services/DischargeLoan";
import TaxIDPage from "./pages/services/TaxID";
import DischargeLoanPaymentPage from "./pages/DischargeLoanPayment";
import TaxIDPaymentPage from "./pages/TaxIDPayment";
import AffidavitsDeclarations from "./pages/diy/AffidavitsDeclarations";
import StatusCorrectionPassport from "./pages/diy/StatusCorrectionPassport";
import TaxLiabilityRemoval from "./pages/diy/TaxLiabilityRemoval";
import TrustEstateEstablishment from "./pages/diy/TrustEstateEstablishment";
import ForeignTrustEIN from "./pages/diy/ForeignTrustEIN";

import PrivateTransportationAuthority from "./pages/diy/PrivateTransportationAuthority";
import TrustPaymentPage from "./pages/TrustPayment";

import ElevenLabsWidget from "@/components/ElevenLabsWidget";
import { PasswordGate } from "@/components/PasswordGate";
import { AuthProvider } from "@/contexts/AuthContext";
import { ChatProvider, useChat } from "@/contexts/ChatContext";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import Auth from "./pages/Auth";
import Profile from "./pages/Profile";
import PublicProfile from "./pages/PublicProfile";
import Inbox from "./pages/Inbox";
import MessageThread from "./pages/MessageThread";

const queryClient = new QueryClient();


const ScrollToTop = () => {
  const { pathname, hash } = useLocation();
  const navType = useNavigationType();

  useEffect(() => {
    if (navType === 'POP') return; // Preserve scroll position on back/forward
    if (hash) return; // Don't scroll to top if there's a hash (let useScrollToHash handle it)
    
    // Use requestAnimationFrame to ensure the new page has rendered
    requestAnimationFrame(() => {
      window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
    });
  }, [pathname, navType, hash]);

  return null;
};

const AppContent = () => {
  return (
    <Routes>
      {/* Public routes */}
      <Route path="/" element={<Index />} />
      <Route path="/about" element={<AboutPage />} />
      <Route path="/resources-learning" element={<ResourcesPage />} />
      <Route path="/services" element={<ProcessesPage />} />
      <Route path="/work-with-us" element={<WorkWithUsPage />} />
      <Route path="/maxims-of-law" element={<MaximsOverviewPage />} />
      <Route path="/maxims-of-equity" element={<MaximsOfEquityPage />} />
      <Route path="/maxims-of-commercial-law" element={<MaximsPage />} />
      <Route path="/faq" element={<FAQPage />} />
      <Route path="/auth" element={<Auth />} />
      <Route path="/litigation" element={<LitigationPage />} />
      <Route path="/phone-consultation" element={<PhoneConsultationPage />} />
      <Route path="/services/discharge-loan" element={<DischargeLoanPage />} />
      <Route path="/services/private-trust" element={<PrivateTrustPage />} />
      <Route path="/services/tax-liability" element={<TaxLiabilityPage />} />
      <Route path="/services/tax-id" element={<TaxIDPage />} />
      <Route path="/discharge-loan-payment" element={<DischargeLoanPaymentPage />} />
      <Route path="/tax-id-payment" element={<TaxIDPaymentPage />} />
      <Route path="/trust-donation" element={<TrustPaymentPage />} />
      
      {/* Protected routes */}
      <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
      <Route path="/inbox" element={<ProtectedRoute><Inbox /></ProtectedRoute>} />
      <Route path="/messages/:userId" element={<ProtectedRoute><MessageThread /></ProtectedRoute>} />
      <Route path="/user/:username" element={<PublicProfile />} />
      <Route path="/back" element={<ProtectedRoute><Home /></ProtectedRoute>} />
      <Route path="/diy-processes" element={<ProtectedRoute><DIYProcessesPage /></ProtectedRoute>} />
      <Route path="/diy-processes/:processId" element={<ProtectedRoute><DIYProcessPage /></ProtectedRoute>} />
      <Route path="/law-library" element={<VideoLibrary />} />
      <Route path="/category/:categoryId" element={<CategoryOptionsPage />} />
      <Route path="/category/:categoryId/videos" element={<VideoCategoryPage />} />
      <Route path="/category/:categoryId/resources" element={<ProtectedRoute><CategoryDocumentsPage /></ProtectedRoute>} />
      <Route path="/category/:categoryId/forum" element={<ProtectedRoute><CategoryForumPage /></ProtectedRoute>} />
      <Route path="/video/:categoryId/:videoSlug" element={<VideoDetailPage />} />
      <Route path="/video/:videoId" element={<VideoDetailPage />} />
      <Route path="/documents-templates" element={<ProtectedRoute><DocumentsTemplates /></ProtectedRoute>} />
      <Route path="/dictionary" element={<DictionaryPage />} />
      <Route path="/dictionary/:term" element={<DictionaryTerm />} />
      <Route path="/ucc" element={<ProtectedRoute><UCCPage /></ProtectedRoute>} />
      
      <Route path="/services/attorney-in-fact" element={<ProtectedRoute><AttorneyInFactPage /></ProtectedRoute>} />
      <Route path="/services/political-status-correction" element={<ProtectedRoute><PoliticalStatusCorrectionPage /></ProtectedRoute>} />
      <Route path="/services/non-citizen-passport" element={<ProtectedRoute><NonCitizenPassportPage /></ProtectedRoute>} />
      <Route path="/services/foreign-trust-banking" element={<ProtectedRoute><ForeignTrustBankingPage /></ProtectedRoute>} />
      <Route path="/services/dot-authority" element={<ProtectedRoute><DOTAuthorityPage /></ProtectedRoute>} />
      <Route path="/diy/affidavits-declarations" element={<ProtectedRoute><AffidavitsDeclarations /></ProtectedRoute>} />
      <Route path="/diy/status-correction-passport" element={<ProtectedRoute><StatusCorrectionPassport /></ProtectedRoute>} />
      <Route path="/diy/tax-liability-removal" element={<ProtectedRoute><TaxLiabilityRemoval /></ProtectedRoute>} />
      <Route path="/diy/trust-estate-establishment" element={<ProtectedRoute><TrustEstateEstablishment /></ProtectedRoute>} />
      <Route path="/diy/foreign-trust-ein" element={<ProtectedRoute><ForeignTrustEIN /></ProtectedRoute>} />
      <Route path="/diy/private-transportation-authority" element={<ProtectedRoute><PrivateTransportationAuthority /></ProtectedRoute>} />
      
      {/* Redirect old video-library URLs to new law-library URLs */}
      <Route path="/video-library" element={<ProtectedRoute><VideoLibrary /></ProtectedRoute>} />
      <Route path="/video-library/:categoryId" element={<ProtectedRoute><VideoCategoryPage /></ProtectedRoute>} />
      <Route path="/law-library/:categoryId" element={<ProtectedRoute><VideoCategoryPage /></ProtectedRoute>} />
      
      {/* Redirect old URLs to new resources-learning URL */}
      <Route path="/resources" element={<ResourcesPage />} />
      <Route path="/law-school" element={<ResourcesPage />} />
      <Route path="/education" element={<ResourcesPage />} />
      
      {/* Redirect old documents URLs */}
      <Route path="/documents/:categoryId" element={<ProtectedRoute><CategoryDocumentsPage /></ProtectedRoute>} />
      
      
      {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
};

const WidgetWrapper = () => {
  const location = useLocation();
  const { isChatOpen } = useChat();
  
  // Hide widget on specific pages, but keep it mounted to preserve conversation state
  const shouldHide = location.pathname === '/auth' || 
                     location.pathname.startsWith('/messages/') ||
                     location.pathname.startsWith('/video/') ||
                     isChatOpen;
  
  return (
    <div style={{ display: shouldHide ? 'none' : 'block' }}>
      <ElevenLabsWidget agentId="agent_8601k0yb68qaf5qs0katr0c3stth" />
    </div>
  );
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <ThemeProvider attribute="class" defaultTheme="light" enableSystem>
      <TooltipProvider>
        <BrowserRouter>
          <AuthProvider>
            <ChatProvider>
              <Toaster />
              <Sonner />
              <ScrollToTop />
              <AppContent />
              <WidgetWrapper />
            </ChatProvider>
          </AuthProvider>
        </BrowserRouter>
      </TooltipProvider>
    </ThemeProvider>
  </QueryClientProvider>
);

export default App;
